import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import request from '../../../services/Request';
import MaterialTable from 'material-table';
import { makeStyles, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox, Grid, Typography, Chip, ButtonGroup, Box } from '@material-ui/core';
import LoadingIcon from '../../items/buttons/LoadingIcon';
import Swal from 'sweetalert2';
import TimezoneSelect from 'react-timezone-select';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(3),
  },
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: '100%',
  },
  divisionSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
  },
  limitItem: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: '#fff',
    borderRadius: 4,
  },
  toggleButtonGroup: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
}));

export default function ManageHFUsers() {
  const classes = useStyles();
  const user = JSON.parse(localStorage.getItem('user'));
  const referTo = user.email;
  let history = useHistory();

  const [hfUsers, setHfUsers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [viewMode, setViewMode] = useState('users'); // 'users' or 'divisions'

  // User Dialog State
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userFormData, setUserFormData] = useState({
    _id: null,
    cardNumber: '',
    surname: '',
    name: '',
    email: '',
    phone: '',
    division: '',
    badgeEnabled: false,
  });
  const [isEditMode, setIsEditMode] = useState(false);

  // Division Dialog State
  const [divisionDialogOpen, setDivisionDialogOpen] = useState(false);
  const [divisionFormData, setDivisionFormData] = useState({
    _id: null,
    name: '',
    isDefault: false,
    timezone: 'UTC',
    limits: [],
  });
  const [isDivisionEditMode, setIsDivisionEditMode] = useState(false);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [refresh]);

  const loadData = async () => {
    try {
      setIsLoading(false);

      // Load HF Users
      const usersRes = await request({
        method: 'get',
        url: `/hfusers/by-account/${referTo}`,
      });
      setHfUsers(usersRes.data);

      // Load Divisions
      const divisionsRes = await request({
        method: 'get',
        url: `/divisions/by-account/${referTo}`,
      });
      setDivisions(divisionsRes.data);

      // Load Categories
      const categoriesRes = await request({
        method: 'get',
        url: `/categories/${referTo}`,
      });
      setCategories(categoriesRes.data);

      setIsLoading(true);
    } catch (error) {
      Swal.fire({
        text: `Error loading data: ${error.message}`,
        icon: 'error',
      });
      setIsLoading(true);
    }
  };

  // User CRUD Functions
  const openUserDialog = (userData = null) => {
    if (userData) {
      setUserFormData({
        _id: userData._id,
        cardNumber: userData.cardNumber,
        surname: userData.surname,
        name: userData.name,
        email: userData.email || '',
        phone: userData.phone || '',
        division: userData.division?._id || '',
        badgeEnabled: userData.badgeEnabled,
      });
      setIsEditMode(true);
    } else {
      setUserFormData({
        _id: null,
        cardNumber: '',
        surname: '',
        name: '',
        email: '',
        phone: '',
        division: divisions.find(d => d.isDefault)?._id || '',
        badgeEnabled: false,
      });
      setIsEditMode(false);
    }
    setUserDialogOpen(true);
  };

  const handleUserFormChange = (field, value) => {
    setUserFormData({ ...userFormData, [field]: value });
  };

  const saveUser = async () => {
    try {
      if (!userFormData.cardNumber || !userFormData.surname || !userFormData.name || !userFormData.division) {
        Swal.fire({
          text: 'Please fill in all required fields',
          icon: 'warning',
        });
        return;
      }

      const url = isEditMode ? `/hfusers/${userFormData._id}` : '/hfusers/create';
      const method = isEditMode ? 'put' : 'post';

      await request({
        method,
        url,
        data: {
          ...userFormData,
          referTo,
        },
      });

      Swal.fire({
        text: `User ${isEditMode ? 'updated' : 'created'} successfully`,
        icon: 'success',
      });

      setUserDialogOpen(false);
      setRefresh(!refresh);
    } catch (error) {
      Swal.fire({
        text: `Error: ${error.response?.data || error.message}`,
        icon: 'error',
      });
    }
  };

  const deleteUser = async (userData) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete user: ${userData.name} ${userData.surname}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        await request({
          method: 'delete',
          url: `/hfusers/${userData._id}`,
        });

        Swal.fire({
          text: 'User deleted successfully',
          icon: 'success',
        });

        setRefresh(!refresh);
      } catch (error) {
        Swal.fire({
          text: `Error: ${error.response?.data || error.message}`,
          icon: 'error',
        });
      }
    }
  };

  // Division CRUD Functions
  const openDivisionDialog = (divisionData = null) => {
    if (divisionData) {
      // Convert limits to use category IDs instead of objects
      const formattedLimits = (divisionData.limits || []).map(limit => ({
        category: limit.category?._id || limit.category || '',
        quantity: limit.quantity,
        period: limit.period,
      }));

      setDivisionFormData({
        _id: divisionData._id,
        name: divisionData.name,
        isDefault: divisionData.isDefault,
        timezone: divisionData.timezone || 'UTC',
        limits: formattedLimits,
      });
      setIsDivisionEditMode(true);
    } else {
      setDivisionFormData({
        _id: null,
        name: '',
        isDefault: false,
        timezone: 'UTC',
        limits: [],
      });
      setIsDivisionEditMode(false);
    }
    setDivisionDialogOpen(true);
  };

  const handleDivisionFormChange = (field, value) => {
    setDivisionFormData({ ...divisionFormData, [field]: value });
  };

  const addLimit = () => {
    setDivisionFormData({
      ...divisionFormData,
      limits: [
        ...divisionFormData.limits,
        { category: '', quantity: 1, period: 'week' },
      ],
    });
  };

  const updateLimit = (index, field, value) => {
    const newLimits = [...divisionFormData.limits];
    newLimits[index][field] = value;
    setDivisionFormData({ ...divisionFormData, limits: newLimits });
  };

  const removeLimit = (index) => {
    const newLimits = divisionFormData.limits.filter((_, i) => i !== index);
    setDivisionFormData({ ...divisionFormData, limits: newLimits });
  };

  const saveDivision = async () => {
    try {
      // Validate limits: category must be selected if limit exists
      const invalidLimits = divisionFormData.limits.filter(
        limit => !limit.category || !limit.category.trim()
      );

      if (invalidLimits.length > 0) {
        Swal.fire({
          text: 'Please select a category for all limits or remove empty limits',
          icon: 'warning',
        });
        return;
      }

      // If name is empty, set to "Default" and mark as default
      const divisionName = divisionFormData.name.trim() || 'Default';
      const isDefaultDivision = divisionName === 'Default' || !divisionFormData.name.trim();

      const url = isDivisionEditMode ? `/divisions/${divisionFormData._id}` : '/divisions/create';
      const method = isDivisionEditMode ? 'put' : 'post';

      await request({
        method,
        url,
        data: {
          name: divisionName,
          isDefault: isDefaultDivision,
          timezone: divisionFormData.timezone || 'UTC',
          limits: divisionFormData.limits,
          referTo,
        },
      });

      Swal.fire({
        text: `Division ${isDivisionEditMode ? 'updated' : 'created'} successfully`,
        icon: 'success',
      });

      setDivisionDialogOpen(false);
      setRefresh(!refresh);
    } catch (error) {
      Swal.fire({
        text: `Error: ${error.response?.data || error.message}`,
        icon: 'error',
      });
    }
  };

  const deleteDivision = async (divisionData) => {
    if (divisionData.isDefault) {
      Swal.fire({
        text: 'Cannot delete the default division',
        icon: 'error',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete division: ${divisionData.name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        await request({
          method: 'delete',
          url: `/divisions/${divisionData._id}`,
        });

        Swal.fire({
          text: 'Division deleted successfully',
          icon: 'success',
        });

        setRefresh(!refresh);
      } catch (error) {
        Swal.fire({
          text: `Error: ${error.response?.data || error.message}`,
          icon: 'error',
        });
      }
    }
  };

  // Table columns
  const userColumns = [
    { title: 'Card Number', field: 'cardNumber' },
    { title: 'Surname', field: 'surname' },
    { title: 'Name', field: 'name' },
    { title: 'Email', field: 'email' },
    { title: 'Phone', field: 'phone' },
    {
      title: 'Division',
      field: 'division.name',
      render: rowData => (
        <Chip
          label={rowData.division?.name || 'Default'}
          color={rowData.division?.isDefault ? 'default' : 'primary'}
          size="small"
        />
      )
    },
    {
      title: 'Badge Enabled',
      field: 'badgeEnabled',
      render: rowData => rowData.badgeEnabled ? 'Yes' : 'No'
    },
  ];

  const divisionColumns = [
    {
      title: 'Division Name',
      field: 'name',
      render: rowData => rowData.name || 'Default'
    },
    {
      title: 'Default',
      field: 'isDefault',
      render: rowData => rowData.isDefault ? 'Yes' : 'No'
    },
    {
      title: 'Limits',
      field: 'limits',
      render: rowData => `${rowData.limits?.length || 0} limit(s)`
    },
  ];

  return (
    <div>
      {!isLoading && <LoadingIcon />}

      {/* Toggle Buttons */}
      <Box className={classes.toggleButtonGroup}>
        <ButtonGroup variant="contained" color="primary">
          <Button
            onClick={() => setViewMode('users')}
            variant={viewMode === 'users' ? 'contained' : 'outlined'}
            color={viewMode === 'users' ? 'primary' : 'default'}
          >
            Users
          </Button>
          <Button
            onClick={() => setViewMode('divisions')}
            variant={viewMode === 'divisions' ? 'contained' : 'outlined'}
            color={viewMode === 'divisions' ? 'primary' : 'default'}
          >
            Divisions
          </Button>
        </ButtonGroup>
      </Box>

      {/* Users Table */}
      {viewMode === 'users' && (
        <MaterialTable
          columns={userColumns}
          data={hfUsers}
          title="Users"
          actions={[
            {
              icon: 'add',
              tooltip: 'Add User',
              isFreeAction: true,
              onClick: () => openUserDialog(),
            },
            {
              icon: 'edit',
              tooltip: 'Edit User',
              onClick: (event, rowData) => openUserDialog(rowData),
            },
            {
              icon: 'delete',
              tooltip: 'Delete User',
              onClick: (event, rowData) => deleteUser(rowData),
            },
          ]}
          options={{
            actionsColumnIndex: -1,
            pageSize: 10,
            pageSizeOptions: [10, 20, 50, 100],
          }}
        />
      )}

      {/* Divisions Table */}
      {viewMode === 'divisions' && (
        <MaterialTable
          columns={divisionColumns}
          data={divisions}
          title="Divisions"
          actions={[
            {
              icon: 'add',
              tooltip: 'Add Division',
              isFreeAction: true,
              onClick: () => openDivisionDialog(),
            },
            {
              icon: 'edit',
              tooltip: 'Edit Division',
              onClick: (event, rowData) => openDivisionDialog(rowData),
            },
            {
              icon: 'delete',
              tooltip: 'Delete Division',
              onClick: (event, rowData) => deleteDivision(rowData),
            },
          ]}
          options={{
            actionsColumnIndex: -1,
            pageSize: 10,
            pageSizeOptions: [10, 20, 50],
          }}
        />
      )}

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Card Number *"
            value={userFormData.cardNumber}
            onChange={(e) => handleUserFormChange('cardNumber', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Surname *"
            value={userFormData.surname}
            onChange={(e) => handleUserFormChange('surname', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Name *"
            value={userFormData.name}
            onChange={(e) => handleUserFormChange('name', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            value={userFormData.email}
            onChange={(e) => handleUserFormChange('email', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Phone"
            value={userFormData.phone}
            onChange={(e) => handleUserFormChange('phone', e.target.value)}
            fullWidth
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Division *</InputLabel>
            <Select
              value={userFormData.division}
              onChange={(e) => handleUserFormChange('division', e.target.value)}
            >
              {divisions.map((div) => (
                <MenuItem key={div._id} value={div._id}>
                  {div.name} {div.isDefault ? '(Default)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={userFormData.badgeEnabled}
                onChange={(e) => handleUserFormChange('badgeEnabled', e.target.checked)}
                color="primary"
              />
            }
            label="Badge Enabled"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveUser} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Division Dialog */}
      <Dialog open={divisionDialogOpen} onClose={() => setDivisionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isDivisionEditMode ? 'Edit Division' : 'Add New Division'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Division Name (leave empty for Default) *"
            value={divisionFormData.name}
            onChange={(e) => handleDivisionFormChange('name', e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., Marketing, Sales, or leave empty for Default"
          />

          <Box marginTop={2} marginBottom={2}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Timezone
            </Typography>
            <TimezoneSelect
              value={divisionFormData.timezone}
              onChange={(timezone) => handleDivisionFormChange('timezone', timezone.value)}
            />
          </Box>

          <div className={classes.divisionSection}>
            <Typography variant="h6" gutterBottom>
              Purchase Limits
            </Typography>
            {divisionFormData.limits.map((limit, index) => (
              <div key={index} className={classes.limitItem}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={limit.category}
                        onChange={(e) => updateLimit(index, 'category', e.target.value)}
                      >
                        {categories.map((cat) => (
                          <MenuItem key={cat._id} value={cat._id}>
                            {cat.name?.en || 'Unknown'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Quantity"
                      type="number"
                      value={limit.quantity}
                      onChange={(e) => updateLimit(index, 'quantity', parseInt(e.target.value))}
                      fullWidth
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth>
                      <InputLabel>Period</InputLabel>
                      <Select
                        value={limit.period}
                        onChange={(e) => updateLimit(index, 'period', e.target.value)}
                      >
                        <MenuItem value="day">Per Day</MenuItem>
                        <MenuItem value="week">Per Week</MenuItem>
                        <MenuItem value="month">Per Month</MenuItem>
                        <MenuItem value="year">Per Year</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <Button onClick={() => removeLimit(index)} color="secondary">
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              </div>
            ))}
            <Button onClick={addLimit} color="primary" style={{ marginTop: 10 }}>
              + Add Limit
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDivisionDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveDivision} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
